import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Receipt,
  ScrollText,
  UtensilsCrossed,
  LogOut,
  Store,
  ChefHat,
  Tag,
  Users,
  Grid,
  UserCheck,
  ShieldCheck,
  Navigation,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
} from "lucide-react";

const links = [
  { to: "/admin/pos", label: "Billing / POS", icon: Receipt, testid: "nav-pos" },
  { to: "/admin/orders", label: "Orders", icon: ScrollText, testid: "nav-orders" },
  { to: "/admin/live-tracking", label: "Live Delivery Radar", icon: Navigation, testid: "nav-live-tracking" },
  { to: "/kitchen", label: "Kitchen Screen", icon: ChefHat, testid: "nav-kitchen" },
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, adminOnly: true, testid: "nav-dashboard" },
  { to: "/admin/customers", label: "Customer CRM", icon: UserCheck, adminOnly: true, testid: "nav-customers" },
  { to: "/admin/tables", label: "Table QR System", icon: Grid, adminOnly: true, testid: "nav-tables" },
  { to: "/admin/menu", label: "Menu", icon: UtensilsCrossed, adminOnly: true, testid: "nav-menu" },
  { to: "/admin/offers", label: "Offers Engine", icon: Tag, adminOnly: true, testid: "nav-offers" },
  { to: "/admin/staff", label: "Staff Management", icon: ShieldCheck, adminOnly: true, testid: "nav-staff" },
];

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Persistent sidebar collapse state (default collapsed on POS to maximize screen width)
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("admin_sidebar_collapsed");
    if (saved !== null) return saved === "true";
    return pathname === "/admin/pos";
  });

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("admin_sidebar_collapsed", collapsed ? "true" : "false");
  }, [collapsed]);

  const visibleLinks = useMemo(
    () => links.filter((l) => !l.adminOnly || user?.role === "admin"),
    [user?.role]
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background">
      {/* Desktop Collapsible Sidebar */}
      <aside
        className={`border-b lg:border-b-0 lg:border-r border-foreground bg-secondary text-secondary-foreground flex lg:flex-col transition-all duration-200 ease-in-out ${
          collapsed ? "lg:w-16" : "lg:w-60"
        } hidden lg:flex`}
      >
        {/* Brand & Toggle Header */}
        <div
          className={`p-3 lg:border-b border-foreground/20 flex items-center justify-between gap-2 ${
            collapsed ? "flex-col py-3 px-1" : "px-4 py-3"
          }`}
        >
          <div className={`flex items-center gap-2.5 min-w-0 ${collapsed ? "justify-center" : ""}`}>
            <img
              src="/logo.png"
              alt="Logo"
              className="h-8 w-8 object-contain rounded-full border border-secondary-foreground/20 shrink-0"
            />
            {!collapsed && (
              <div className="min-w-0">
                <span className="serif text-base font-bold tracking-tight text-primary block leading-none truncate">
                  Velpula Mess
                </span>
                <span className="text-[9px] uppercase font-bold tracking-widest text-secondary-foreground/70 block truncate">
                  Authentic Dining • POS
                </span>
              </div>
            )}
          </div>

          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded hover:bg-secondary-foreground/10 text-secondary-foreground/80 hover:text-secondary-foreground border border-secondary-foreground/20 transition shrink-0"
            title={collapsed ? "Expand Sidebar" : "Collapse Sidebar (Full POS Width)"}
          >
            {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex flex-col p-1.5 gap-1 overflow-y-auto">
          {visibleLinks.map(({ to, label, icon: Icon, testid }) => {
            const isActive = pathname === to;
            return (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                title={collapsed ? label : undefined}
                className={`flex items-center gap-3 px-3 py-2.5 text-xs font-semibold transition-colors rounded ${
                  collapsed ? "justify-center px-0" : ""
                } ${
                  isActive
                    ? "bg-primary text-primary-foreground font-bold shadow-xs"
                    : "hover:bg-secondary-foreground/10 text-secondary-foreground"
                }`}
              >
                <Icon size={18} className="shrink-0" />
                {!collapsed && <span className="truncate">{label}</span>}
              </Link>
            );
          })}

          <a
            href="/"
            data-testid="nav-storefront"
            title={collapsed ? "View Website" : undefined}
            className={`flex items-center gap-3 px-3 py-2.5 text-xs font-semibold hover:bg-secondary-foreground/10 text-secondary-foreground transition-colors rounded ${
              collapsed ? "justify-center px-0" : ""
            }`}
          >
            <Store size={18} className="shrink-0" />
            {!collapsed && <span className="truncate">View Website</span>}
          </a>
        </nav>

        {/* Footer User / Logout */}
        <div className="mt-auto p-2 lg:border-t border-foreground/20 space-y-2">
          <div className={`flex items-center gap-2 ${collapsed ? "flex-col justify-center" : "px-1"}`}>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold truncate">{user?.name}</p>
                <p className="text-[10px] uppercase font-bold text-secondary-foreground/60 tracking-wider truncate">
                  {user?.role}
                </p>
              </div>
            )}
            <button
              data-testid="logout-button"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="p-2 border border-secondary-foreground/30 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors rounded shrink-0"
              title="Log out"
            >
              <LogOut size={15} />
            </button>
          </div>
          {!collapsed && (
            <div className="text-[9px] text-secondary-foreground/50 text-center uppercase tracking-widest pt-1 border-t border-secondary-foreground/10 font-bold">
              Powered by NovaSaaS
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <div className="lg:hidden flex items-center justify-between border-b border-foreground bg-secondary text-secondary-foreground px-3 py-2">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Logo" className="h-7 w-7 object-contain rounded-full" />
          <span className="serif text-sm font-bold text-primary">Velpula Mess</span>
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-1.5 border border-foreground/20 rounded bg-background text-foreground"
        >
          {mobileOpen ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>

      {/* Mobile Nav Links Dropdown / Drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-b border-foreground bg-card p-2 grid grid-cols-2 gap-1 animate-in slide-in-from-top-2">
          {visibleLinks.map(({ to, label, testid }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              data-testid={`${testid}-mobile`}
              className={`px-3 py-2 text-xs font-bold rounded ${
                pathname === to ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"
              }`}
            >
              {label}
            </Link>
          ))}
          <a
            href="/"
            className="px-3 py-2 text-xs font-bold hover:bg-muted text-foreground rounded flex items-center gap-1"
          >
            <Store size={13} /> View Website
          </a>
        </div>
      )}

      {/* Main Content Area (Maximizes width for POS) */}
      <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
    </div>
  );
};

export default AdminLayout;

